/**
 * Owner 2026-10-22 01:04 ("kolumna marka zmniejsza się mocno … nie ścieśniaj, zostaw przewijane okno"): the quote's
 * table never gets narrower than its fixed columns, the 15% of "Opis" and `NAME_MIN_REM` for "Marka" (enough for
 * "Alcons Audio QR24 Pro-Ribbon" on one or two lines); in a narrower card it scrolls sideways (`.table-wrap`).
 * The widths are those of quotes.css (`.q-col-*`).
 */
export const NAME_MIN_REM = 11.5;
/**
 * Owner 2026-10-23 15:21 („rozsunąć kolumny urządzenie/usługa i marka”) and 16:07 („może być troszkę większy ten
 * trybik”): the gutter between "Urządzenie / Usługa" and "Marka" where the ⚙ of a line and of the "Dodaj…" field sits
 * (16 px), so it never moves the name or the field (quotes.css `--q-gear`). With the cells' own padding the gap is ~25 px.
 * The section column keeps its total of 9.25rem (its text gives up the 4 px): the whole quote must still fit a full
 * window of 1280 px (owner 2026-10-22; e2e quote-editor "fits at 1280"), and 0.25rem more would already scroll there.
 */
export const LINE_GEAR_REM = 0.5;
export const SECTION_BASE_REM = 8.75;
export const SECTION_REM = SECTION_BASE_REM + LINE_GEAR_REM;
const FIXED = { section: SECTION_REM, qty: 3.5, days: 3.5, price: 6, discount: 3.5, net: 7.25, cost: 5.5, supplier: 10, actions: 2.75 } as const;
const DESC_SHARE = 0.15;

export type ShownColumns = { price: boolean; discount: boolean; net: boolean; cost: boolean; supplier: boolean; actions: boolean };

/** The table's min-width in rem, rounded up to a quarter; `nameRem`: room "Marka" needs (`nameNeedRem`), at least NAME_MIN_REM. */
export function quoteTableMinRem(c: ShownColumns, nameRem: number = NAME_MIN_REM): number {
  const fixed =
    FIXED.section + FIXED.qty + FIXED.days + (c.price ? FIXED.price : 0) + (c.discount ? FIXED.discount : 0) + (c.net ? FIXED.net : 0) + (c.cost ? FIXED.cost : 0) + (c.supplier ? FIXED.supplier : 0) + (c.actions ? FIXED.actions : 0);
  return Math.ceil(((fixed + Math.max(NAME_MIN_REM, nameRem)) / (1 - DESC_SHARE)) * 4) / 4;
}

/**
 * Owner 2026-10-23 16:22 („pole dodaj może być większe jeśli któraś pozycja będzie miała dłuższy tytuł. dopóki nie
 * będzie to niech jest tak jak jest … pojawia się pozycja z dłuższym tytułem - dopasowujesz kolumnę”): "Marka" (and the
 * "Dodaj…" field under it) grows for a long title. A title may take two lines, as before (NAME_MIN_REM is enough for
 * "Alcons Audio QR24 Pro-Ribbon"); only one that would need three gets a wider column, up to NAME_MAX_REM, after which it
 * wraps again. The table then scrolls sideways where the window is too narrow (owner 2026-10-22: never squeezed).
 * The width is the text's own (canvas `measureText` in the quote's font), not a layout read of the page.
 */
export const NAME_MAX_REM = 24;
/** the cell's padding (6 px × 2) and the text's inset (5 px × 2) */
const NAME_INSET_REM = 22 / 16;
export type MeasureText = (text: string) => number;

export function nameNeedRem(names: string[], measure: MeasureText = measureBody): number {
  let need = NAME_MIN_REM;
  for (const n of names) {
    const longestWord = Math.max(0, ...n.split(/\s+/).map(measure));
    const px = Math.max(longestWord, measure(n) / 2);
    need = Math.max(need, px / 16 + NAME_INSET_REM);
  }
  return Math.min(NAME_MAX_REM, Math.ceil(need * 4) / 4);
}

let ctx: CanvasRenderingContext2D | null | undefined;
/** The width in px of `text` in the body font (15 px); without a canvas (tests) about 0.55 em a character. */
function measureBody(text: string): number {
  if (ctx === undefined) {
    try {
      ctx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
      if (ctx) ctx.font = `400 15px ${getComputedStyle(document.body).fontFamily || 'sans-serif'}`;
    } catch {
      ctx = null;
    }
  }
  return ctx ? ctx.measureText(text).width : text.length * 15 * 0.55;
}
